import { useRef } from "react";

import { FileDownloadStatus } from "@/middleware/hooks/attachments/useFileDownload";
import { cn } from "@/lib/utils";

import { File, FileDown, Loader } from "lucide-react";

// Below this the arc is too short to read as a ring — at 0.5% it sweeps under
// two degrees, which paints as nothing once the 3px mask is applied. Keep the
// spinner until there is something to see.
const MIN_VISIBLE_RING_PERCENT = 1;

type Props = {
  /**
   * Download state for documents whose click downloads (owned by the parent,
   * which handles the click). Previewable PDFs open the viewer instead and stay
   * "idle" here.
   */
  downloadStatus?: FileDownloadStatus;
  /**
   * True when clicking this card downloads the file instead of opening the
   * in-app viewer — every non-PDF document, plus PDFs above the preview ceiling
   * (`PDF_PREVIEW_MAX_BYTES`). Swaps the glyph so the card advertises what the
   * tap will actually do; without it a 95 MB PDF looks identical to a 1 MB one
   * that opens inline.
   */
  willDownload?: boolean;
  /**
   * 0-100 byte progress for an in-flight download, or undefined when unknown.
   * Renders a determinate ring instead of the spinner: a 95 MB document takes
   * minutes, and an indeterminate spinner for that long is indistinguishable
   * from a hang.
   */
  progress?: number;
};

export default function FileAttachment({
  downloadStatus = "idle",
  willDownload = false,
  progress,
}: Props) {
  // Note: the full file is NO LONGER fetched on render — documents download
  // only when the card is clicked (see useFileDownload). This card just
  // reflects that click's state.
  //
  // GLYPH = what a click DOES. Plain file when it opens the viewer,
  // file-with-arrow when it saves to disk.
  //
  // COLOUR = neutral throughout, red only on error. Deliberately NO success
  // colour: the file leaves the app's control once saved, so we can't know it's
  // still there, and for a downloadable card the action stays repeatable — a
  // green "done" would imply a persistent state that doesn't exist.
  const Glyph = willDownload ? FileDown : File;
  const isDownloading = downloadStatus === "downloading";

  // Hold the last value seen while downloading. `processFileAttachment` clears
  // its progress entry in a `finally`, which runs as the promise the hook is
  // awaiting settles — but the hook stays "downloading" until `saveFile`
  // resolves after that. Without the latch the ring would drop back to the
  // indeterminate spinner for that gap: one frame on desktop, but on iOS
  // `saveFile` awaits the share sheet, so the card would spin behind it until
  // the user picks an action.
  const lastProgress = useRef<number | undefined>(undefined);
  if (!isDownloading) {
    lastProgress.current = undefined;
  } else if (progress !== undefined) {
    lastProgress.current = progress;
  }

  const shownProgress = isDownloading
    ? (progress ?? lastProgress.current)
    : undefined;
  const hasRing =
    shownProgress !== undefined && shownProgress >= MIN_VISIBLE_RING_PERCENT;

  return (
    <div className="relative aspect-square size-14 shrink-0 rounded-xl">
      <div className="z-5 absolute left-0 top-0 flex aspect-square size-full items-center justify-center rounded-sm align-middle lg:cursor-pointer">
        {isDownloading && !hasRing ? (
          // Either no determinate total (no Content-Length and no fileSize in
          // the meta) or an arc too small to see yet — fall back to the busy
          // spinner in the same neutral as idle. Motion matters most here:
          // early progress on a large file rounds to 0, and a motionless card
          // is indistinguishable from a hung one.
          <Loader className="size-7 animate-spin text-muted-foreground" />
        ) : (
          <Glyph
            strokeWidth={1}
            className={cn(
              "size-full transition-colors",
              downloadStatus === "error"
                ? "text-destructive"
                : "text-muted-foreground",
              // Dim the glyph under the ring so the ring reads as the primary
              // signal while bytes are moving.
              hasRing && "opacity-40",
            )}
          />
        )}

        {hasRing && (
          // Conic-gradient ring: no extra SVG, and it animates by class alone.
          // The unfilled remainder is a faint track rather than transparent, so
          // the control still reads as a ring at low percentages and the card
          // does not look like it lost its glyph.
          <div
            className="pointer-events-none absolute inset-0 rounded-full"
            style={{
              background: `conic-gradient(currentColor ${shownProgress}%, rgb(128 128 128 / 0.25) ${shownProgress}%)`,
              mask: "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
              WebkitMask:
                "radial-gradient(farthest-side, transparent calc(100% - 3px), #000 calc(100% - 3px))",
            }}
            role="progressbar"
            aria-valuenow={Math.round(shownProgress)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        )}
      </div>
    </div>
  );
}
